import * as vp from 'vopay';
import { useMemo, JSX } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import { useLockedFeatureDialogue } from './providers/LockedFeatureDialogue.provider';
import { useConfirmationDialogue } from './providers/ConfirmationDialogue.provider';
import { useClientLogin } from './providers/ClientLogin.provider';
import useDataFetchIndicator from './hooks/useDataFetchIndicator';
import AuthUserRoute from './components/LoginPages/AuthUser/Routes';
import DocsAuthenticationRoute from './components/DocsAuthentication/Route';
import styles from './App.module.scss';
import useZendesk from './hooks/useZendesk';

const { ConfirmationDialogue, LoadingIndicator, LockedFeatureDialogue, useTranslationSetup } = vp;

const App = (): JSX.Element => {
  useTranslationSetup();

  const dialogueProps = useConfirmationDialogue();
  const history = useHistory();
  const location = useLocation(); // Get the current location
  const { isAuthenticated } = useClientLogin();
  const { isLocked, updateIsLocked, lockedFeature } = useLockedFeatureDialogue();
  const [[isFetchingData, loadingMessage, indicator, isDataFetchingSticky]] = useDataFetchIndicator();
  useZendesk();
  // Check if the current route is '/docs'
  const isDocsRoute = location.pathname === '/docs';

  return (
    <div className={styles.app}>
      {useMemo(
        () => (
          <ConfirmationDialogue {...dialogueProps} />
        ),
        [dialogueProps],
      )}

      {useMemo(
        () => (
          <LockedFeatureDialogue
            lockedFeature={lockedFeature}
            isVisible={isLocked}
            cancel={() => updateIsLocked(false, '')}
            contactOutTeam={() => {
              history.push({
                pathname: '/create-tickets',
                state: { ticketSubject: `Enquiry about ${lockedFeature} feature` },
              });
              updateIsLocked(false, '');
            }}
          />
        ),
        [isLocked],
      )}
      <AuthUserRoute />
      <DocsAuthenticationRoute />

      {/* Render Login or Dashboard only if the current route is NOT '/docs' */}
      {!isDocsRoute && isAuthenticated === false && <Login />}
      {!isDocsRoute && isAuthenticated === true && <Dashboard />}

      {useMemo(
        () => (
          <LoadingIndicator
            size="lg"
            isVisible={
              (isFetchingData && (indicator === 'fullScreen' || indicator === undefined)) || !!isDataFetchingSticky
            }
            message={loadingMessage}
            noLeadingDots
            hasNoMessage
            hasNoTitle
          />
        ),
        [isFetchingData, isDataFetchingSticky],
      )}
    </div>
  );
};

export default App;
